/**
 * Identifiers for the different types of requests that can be made to the player.
 *
 * @remarks
 * The player may request headers for some requests during its lifecycle.
 * The player will use getRequestHeader(requestType: RequestType) to retrieve the headers for a request.
 *
 * The client application has to register a callback for the player via setRequestHeaderCallback(callback: RequestHeaderCallback).
 * The client should then be able to return the headers for the specified request types.
 *
 * You can omit some request types if you don't use the features at all.
 * You can also return default headers independent of the request type.
 */
export const RequestType = {
  /**
   * Request to the DRM endpoint or a license server
   *
   * @remarks
   * Every client application, which uses DRM, has to implement this request type.
   *
   * This may only be omitted if the player is not used with DRM or your free/anonymous users does not have user tokens.
   *
   * For RTL this should return the following headers:
   * 'x-auth-token': A valid user JWT
   */
  DRM: 'foundation.player.drm',

  /**
   * Request from the entitlement plugin
   *
   * @remarks
   * Every client that uses DRM must also support this request type.
   * The request headers for this request type are same as for the DRM request type.
   *
   * This may only be omitted if the player is not used with DRM or your free/anonymous users does not have user tokens.
   *
   * This should return the following headers:
   * 'x-auth-token': A valid user JWT
   */
  Entitlement: 'foundation.plugin.entitlement',

  /**
   * Request from the webhook plugin
   *
   * @remarks
   * This is only required if you use the webhook plugin and need custom headers for the webhook requests.
   *
   * @deprecated This is deprecated and was never in use. Please remove this from your implementation.
   */
  Webhook: 'foundation.plugin.webhook',

  /**
   * Request from the concurrent streamcheck plugin
   *
   * @remarks
   * This is only required if you use the concurrent streamcheck and your free/anonymous users does not have user tokens.
   *
   * This should return the following values:
   * 'jwt': A valid user JWT
   */
  ConcurrentStreamCheck: 'foundation.plugin.concurrentStreamCheck',

  /**
   * Request from the nielsen plugin
   *
   * @remarks
   * This is only required if you use the nielsen plugin.
   *
   * This should return the following headers:
   * 'x-auth-token': A valid user JWT
   */
  Nielsen: 'foundation.plugin.nielsen',
} as const;

/**
 * {@inheritDoc RequestType:var}
 */
export type RequestType = (typeof RequestType)[keyof typeof RequestType];
